/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import {  AttributeValue, CategoryPathLookup } from '@msdyn365-commerce/retail-proxy';
import {
    getAttributeValuesAsync, getCategoryPathsAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

import {
    ActiveCartProductsInput,
    getActiveCartProductsAction
} from '@msdyn365-commerce-modules/retail-actions';
import { hasArray } from '../Utilities/emtpyChecker';

// tslint:disable-next-line: completed-docs
export class AttributesForSelectedVariantInput implements IActionInput {
    public channelId: number;
    public catalogId: number;

    constructor(channelId: number, catalogId?: number) {
        this.channelId = channelId;
        this.catalogId = catalogId || 0;
    }

    public getCacheKey = () => `AttributesForSelectedVariant`;
    public getCacheObjectType = () => 'AttributesForSelectedVariant';
    public dataCacheType = (): CacheType => 'request';
}

export const createAttributesForSelectedVariantInput = (inputData: ICreateActionContext<IGeneric<IAny>>): AttributesForSelectedVariantInput => {

    return new AttributesForSelectedVariantInput(+inputData.requestContext.apiSettings.channelId);
};

export interface IProductWithAttribute {
    productId: number;
    attributeValue: AttributeValue[];
    Name:string | undefined;
    PrimaryImageUrl: string | undefined;
    categoryName: string | undefined;
}

/**
 * Calls the Retail API to get the product attributes for items in the cart.
 * @param input
 * @param ctx
 */
export async function getAttributesForCartLineItems(
    input: AttributesForSelectedVariantInput,
    ctx: IActionContext
): Promise<IProductWithAttribute[]> {
    // let productAttributes: AttributeValue[][] = [];
    // let productAttributes: number[] = [];
    const productAttributes: IProductWithAttribute[] = [];

    const products =  (await getActiveCartProductsAction(new ActiveCartProductsInput(), ctx)).filter(Boolean);
    const catPaths: CategoryPathLookup[] = [];
    const idArray: any[] = [];
    products.map(items => idArray.push(items.RecordId));
    if(!hasArray(idArray)) {
        return [];
    }
    for (let productsIter = 0; productsIter < idArray.length; productsIter++) {
        catPaths.push({ ProductId: idArray[productsIter] });
    }
    for(let i = 0 ; i < products?.length ; i++) {
        const productId = products[i].RecordId;
        const Name = products[i].Name;
        const PrimaryImageUrl = products[i].PrimaryImageUrl;
        try {
            const productSpecificationData: AttributeValue[] = await getAttributeValuesAsync({ callerContext: ctx, queryResultSettings: {} }, productId, ctx.requestContext.apiSettings.channelId, ctx.requestContext.apiSettings.catalogId);
            const attributeValue = productSpecificationData.filter(Boolean);

            const getCategory = await getCategoryPathsAsync({ callerContext: ctx, queryResultSettings: { Paging: { Top: idArray.length }, Sorting: {} } }, ctx.requestContext.apiSettings.channelId, ctx.requestContext.apiSettings.catalogId, catPaths);
            const checkCategory = getCategory?.find(data => data.ProductId === products[i].RecordId);
            const getSegments = checkCategory && checkCategory.CategoryPath && checkCategory.CategoryPath[0]?.Segments;
            let categoryName;
            if (getSegments) {
                categoryName = getSegments[getSegments?.length - 1]?.CategoryName!;
            }
            productAttributes.push({
                productId,
                attributeValue,
                Name,
                PrimaryImageUrl,
                categoryName
            });
        } catch (error) {
            throw new Error(`Unable to get data:  ${error}`);
        }
    }

    return productAttributes;
}

/**
 * Calls the Retail API to get the product availabilities for items in the cart.
 * @param input
 * @param ctx
 */

export const getAvailabilitiesForCartLineItemsDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-product-attributes-cartlines',
    action: <IAction<IProductWithAttribute[]>>getAttributesForCartLineItems,
    input: createAttributesForSelectedVariantInput
});

export default getAvailabilitiesForCartLineItemsDataAction;
